import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useTranslation } from 'react-i18next';

import FilesService from '@services/FilesService';
import { assetFileName, looksLikeImageUrl } from '@helpers/assetUrls';

import { useInspectorStyles } from './styles';

export interface IResourceUploadFieldProps {
  value: string;
  onChange: (next: string) => void;
  accept?: string;
  disabled?: boolean;
  testIdPrefix: string;
  uploadLabel?: string;
  unsupportedLabel?: string;
  onUploadingChange?: (isUploading: boolean) => void;
  previewKind?: 'image' | 'file' | 'auto';
}

/** Stored on an upload the browser gave no type; the presign signs whatever it is handed. */
const UNTYPED_UPLOAD_CONTENT_TYPE = 'application/octet-stream';

/**
 * Tells whether one `accept` rule covers the picked file.
 *
 * A MIME rule also matches on the extension it implies, and does so even when
 * the file carries a type of its own: a browser hands back whatever the OS
 * claims, and a plainly valid PNG arrives as `image/x-png` or
 * `application/octet-stream` often enough that refusing it on the type alone
 * would leave those machines unable to upload anything.
 *
 * @param rule - One lower-cased entry of the `accept` list.
 * @param type - The file's MIME type, lower-cased and possibly empty.
 * @param name - The file's name, lower-cased.
 * @returns Whether the rule accepts the file.
 */
const ruleMatches = (rule: string, type: string, name: string): boolean => {
  if (rule.startsWith('.')) {
    return name.endsWith(rule);
  }

  if (rule.endsWith('/*')) {
    return type.startsWith(rule.slice(0, -1));
  }

  if (type === rule) {
    return true;
  }

  const subtype = rule.split('/')[1] ?? '';

  return (
    (!!subtype && name.endsWith(`.${subtype}`)) ||
    (rule === 'image/jpeg' && name.endsWith('.jpg'))
  );
};

/**
 * Tells whether the picked file satisfies the field's `accept` list.
 *
 * @param file - File chosen in the hidden input.
 * @param accept - The field's `accept` attribute; absent, every file passes.
 * @returns Whether the file may be uploaded.
 */
const isFileAccepted = (file: File, accept?: string): boolean => {
  const rules = (accept ?? '')
    .split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean);

  if (!rules.length) {
    return true;
  }

  const type = (file.type ?? '').toLowerCase();
  const name = (file.name ?? '').trim().toLowerCase();

  return rules.some((rule) => ruleMatches(rule, type, name));
};

/**
 * Uploads a file and stores the URL it lands on — the editor's one way to put
 * a resource behind a value that used to be typed in by hand.
 *
 * The upload goes through `onChange`, so the caller's existing save path (a
 * debounced autosave, or an explicit Save) persists it exactly as it persisted
 * a pasted URL.
 *
 * @param props.value - Stored URL; `''` means nothing is set.
 * @param props.onChange - Called with the new URL, or `''` when it is removed.
 * @param props.accept - `accept` attribute, also used to validate the pick;
 * absent, any file may be uploaded.
 * @param props.disabled - Whether editing is blocked.
 * @param props.testIdPrefix - Prefix for the row's `data-testid`s.
 * @param props.uploadLabel - Overrides the upload button's label.
 * @param props.unsupportedLabel - Overrides the refused-format message, which
 * is the only place the accepted formats can be named for this field.
 * @param props.onUploadingChange - Called as an upload starts and ends, so the
 * caller can block the controls that would race it.
 * @param props.previewKind - `'image'` (default) previews the value; `'file'`
 * shows the uploaded file's name; `'auto'` shows the name, and the preview
 * beside it when the URL names an image.
 */
const ResourceUploadField = ({
  value,
  onChange,
  accept,
  disabled = false,
  testIdPrefix,
  uploadLabel,
  unsupportedLabel,
  onUploadingChange,
  previewKind = 'image',
}: IResourceUploadFieldProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // The message belongs to the pick it came from; once the value moves on it
  // describes an operation that is no longer on screen.
  useEffect(() => {
    setUploadError(null);
  }, [value]);

  const label = uploadLabel ?? translate('editorMode.upload', 'Upload');

  /**
   * Validates the pick, uploads it, and hands the resulting URL to `onChange`.
   *
   * @param event - Change event of the hidden file input.
   */
  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again still fires a change event.
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!isFileAccepted(file, accept)) {
      setUploadError(
        unsupportedLabel ??
          translate(
            'editorMode.resourceUploadUnsupported',
            'This file type is not accepted here.',
          ),
      );
      return;
    }

    // A machine with no registered type for the extension reports none.
    const upload = file.type
      ? file
      : new File([file], file.name, { type: UNTYPED_UPLOAD_CONTENT_TYPE });

    setUploadError(null);
    setUploadProgress(0);
    setIsUploading(true);
    onUploadingChange?.(true);

    try {
      const [uploadInfo] = await FilesService.uploadFilesWithProgress(
        [upload],
        (progress: number) => setUploadProgress(progress),
      );

      // A resolved upload that carries no URL is a failure that merely did not
      // throw. Reporting it is the whole point: the progress bar has just run
      // to the end, so silence reads as success and the author walks away
      // believing the file is stored.
      if (!uploadInfo?.url) {
        throw new Error('the upload returned no URL');
      }

      onChange(uploadInfo.url);
    } catch (uploadFailure) {
      console.error(
        `Failed to upload editor resource: ${
          (uploadFailure as Error)?.message
        }`,
        { testIdPrefix },
      );
      setUploadError(
        translate(
          'editorMode.resourceUploadFailed',
          'Upload failed. Try again.',
        ),
      );
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
    }
  };

  const renderPreview = () => {
    if (!value) {
      return (
        <Typography
          sx={styles.resourceUploadPlaceholder}
          data-testid={`${testIdPrefix}-preview`}
        >
          {translate('editorMode.resourceNoFile', 'No file selected')}
        </Typography>
      );
    }

    const thumbnail = (testId: string) => (
      <Box
        sx={{
          ...styles.resourceUploadPreview,
          // A quote inside the value would close the CSS string and drop the
          // whole declaration, leaving a preview that is silently blank.
          backgroundImage: `url("${value.replace(/"/g, '%22')}")`,
        }}
        data-testid={testId}
      />
    );

    if (previewKind === 'image') {
      return thumbnail(`${testIdPrefix}-preview`);
    }

    return (
      <>
        {previewKind === 'auto' && looksLikeImageUrl(value)
          ? thumbnail(`${testIdPrefix}-thumbnail`)
          : null}
        <Typography
          sx={styles.resourceUploadFileName}
          title={value}
          data-testid={`${testIdPrefix}-preview`}
        >
          {assetFileName(value)}
        </Typography>
      </>
    );
  };

  return (
    <Box sx={styles.resourceUploadField}>
      <Box sx={styles.resourceUploadRow}>
        {renderPreview()}
        <Box sx={styles.resourceUploadActions}>
          <Tooltip title={label} placement="bottom" enterDelay={400}>
            <Box component="span">
              <IconButton
                size="small"
                component="label"
                disabled={disabled || isUploading}
                sx={styles.iconSquareButton}
                aria-label={label}
                data-testid={`${testIdPrefix}-upload-button`}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 15 }} />
                <input
                  hidden
                  type="file"
                  accept={accept}
                  onChange={handleFileSelected}
                  data-testid={`${testIdPrefix}-upload-input`}
                />
              </IconButton>
            </Box>
          </Tooltip>
          {!!value && (
            <IconButton
              size="small"
              disabled={disabled || isUploading}
              onClick={() => onChange('')}
              sx={[styles.iconSquareButton, styles.iconDangerButton]}
              aria-label={translate('editorMode.resourceRemove', 'Remove')}
              data-testid={`${testIdPrefix}-remove-button`}
            >
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {isUploading && (
        <LinearProgress
          variant="determinate"
          value={uploadProgress}
          sx={styles.compactProgressBar}
        />
      )}

      {uploadError && (
        <Typography
          sx={styles.helperError}
          data-testid={`${testIdPrefix}-error`}
        >
          {uploadError}
        </Typography>
      )}
    </Box>
  );
};

export default ResourceUploadField;
